import React, { useEffect, useState } from "react";
import { CalendarCheck } from "lucide-react";

import classNames from "classnames";
import dayjs from "dayjs";
import Card, { CardBody, CardHeader, CardLabel, CardTitle } from "../../components/bootstrap/Card";
import Button from "../../components/bootstrap/Button";
import PaginationButtons from "../../components/PaginationButton";
import { useParams } from "react-router-dom";
import { centralApi } from "../AxiosInstance/CreateInstance";
import Spinner from "../../components/bootstrap/Spinner";

const InterviewDetailsTable = ({
    sortedItems,
    activeTab,
    intDetail,
    darkModeStatus,
    currentPage,
    setCurrentPage,
    perPage,
    setPerPage,
    totalItems,
    handleUpdateView,
    handleDelModal,
    refreshRounds
}: any) => {
    const { id } = useParams();
    const [scheduledRounds, setScheduledRounds] = useState<Set<string>>(new Set());
    const [loadingRounds, setLoadingRounds] = useState(true);


    function capitalizeWords(str: string | undefined): string {
        if (!str) return '';
        return str
            .toLowerCase()
            .split(' ')
            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    useEffect(() => {
        if (!id || !intDetail?.length) return;

        const fetchCandidates = async () => {
            try {
                setLoadingRounds(true);
                const response = await centralApi.get(
                    `/interviewService/stages/candidate-applications/all`
                );

                const candidates = response.data?.data || [];
                const matchingCandidates = candidates.filter((candidate: any) =>
                    candidate.applications.some((app: any) => app.requisitionID === id)
                );
                const roundStatusMap = new Map<string, { result: string; date: string }>();
                matchingCandidates.forEach((candidate: any) => {
                    candidate.applications.forEach((app: any) => {
                        if (app.requisitionID === id) {
                            (app.stages || [])
                                .filter(
                                    (stg: any) =>
                                        stg.roundType !== "-" && stg.date !== null
                                )
                                .forEach((stg: any) => {
                                    const rt = stg.roundType?.trim()?.toUpperCase();
                                    const prev = roundStatusMap.get(rt);
                                    if (
                                        !prev ||
                                        new Date(stg.date) > new Date(prev.date)
                                    ) {
                                        roundStatusMap.set(rt, {
                                            result: stg.result?.toLowerCase(),
                                            date: stg.date,
                                        });
                                    }
                                });
                        }
                    });
                });
                const scheduled = new Set(
                    Array.from(roundStatusMap.entries())
                        .filter(([_, value]) => value.result !== "shortlisted")
                        .map(([key]) => key)
                );

                setScheduledRounds(scheduled);
            } catch (error) {
                // console.error("Error fetching candidates:", error);
            } finally {
                setLoadingRounds(false);
            }
        };

        fetchCandidates();
    }, [id, intDetail, refreshRounds]);

    if (sortedItems.length === 0 || activeTab !== "ATTACHINTERVIEW") {
        return null;
    }

    return (
        <Card>
            <CardHeader borderSize={1} size="sm">
                <CardLabel iconColor="info" className="ms-2">
                    <CardTitle>
                        <>
                            <CalendarCheck className="me-2 text-info" />
                            Interview Details
                        </>
                    </CardTitle>
                </CardLabel>
            </CardHeader>
            <CardBody className="table-responsive">
                <table className="table table-modern">
                    <thead>
                        <tr>
                            <th style={{ width: "10%" }}>Round Type</th>
                            <th style={{ width: "10%" }}>Interviewers</th>
                            <th style={{ width: "10%" }}>
                                <span className="mx-3">Mode</span>
                            </th>
                            <th style={{ width: "15%" }}>Scheduled Time</th>
                            <th style={{ width: "15%" }}>Duration</th>
                            <th style={{ width: "20%" }} className="text-end pe-4">
                                Action
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {intDetail.map((item: any) => (
                            <tr key={item.id}>
                                <td>{capitalizeWords(item.roundType)}</td>
                                <td>
                                    {item.interviewers && Array.isArray(item.interviewers)
                                        ? item.interviewers.map((i: any) => i.name).join(", ")
                                        : ""}
                                </td>
                                <td>
                                    <Button
                                        isLink
                                        color={item.mode === "online" ? "success" : "info"}
                                        style={{
                                            borderRadius: "0.5rem",
                                            backgroundColor: "dark",
                                            fontWeight: "600",
                                        }}
                                        className="text-nowrap"
                                    >
                                        {capitalizeWords(item.mode) || "-"}
                                    </Button>
                                </td>
                                <td>
                                    {dayjs(item.scheduledTime, "HH:mm").format("hh:mm A") ||
                                        "-"}
                                </td>
                                <td>{`${item?.interviewLength} min` || "-"}</td>
                                <td className="text-end pe-4">
                                    <div className="d-flex justify-content-end gap-2">
                                        <Button
                                            icon="Edit"
                                            isOutline={!darkModeStatus}
                                            isLight={darkModeStatus}
                                            className="btn btn-light-info"
                                            onClick={() => handleUpdateView(item)}
                                        />
                                        {loadingRounds ? <div className="mt-2"><Spinner color='danger' isSmall inButton isGrow /></div> : <Button
                                            icon='Delete'
                                            isDisable={loadingRounds || scheduledRounds.has(item.roundType?.trim()?.toUpperCase())}
                                            isOutline={!darkModeStatus}
                                            isLight={darkModeStatus}
                                            onClick={() => handleDelModal(item)}
                                            className="btn btn-light-danger"
                                        />}

                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </CardBody>
            <PaginationButtons
                label="Interview List"
                setCurrentPage={setCurrentPage}
                currentPage={currentPage}
                perPage={perPage}
                setPerPage={setPerPage}
                totalCount={totalItems}
            />
        </Card>
    );
};

export default InterviewDetailsTable;